import type { CartAttribute, CartLine, CartLineInput, GiftBox, Money } from "./shopify/types";
import { sumMoney } from "./format";

// A box in the cart is a set of ordinary Shopify cart lines tied together by
// line attributes. Keys starting with "_" are hidden from the buyer at
// checkout; the others show up on the order so whoever packs it knows what
// goes where. Boxes we pack ourselves (a collection) also carry the
// collection's handle and title; boxes from the builder don't.
export const BOX_ATTR = {
  id: "_box_id",
  role: "_box_role",
  collection: "_box_collection",
  title: "_box_title",
  label: "Gift box",
  recipient: "Recipient",
  message: "Gift message",
  sender: "From",
} as const;

export type BoxRole = "box" | "item" | "card";

export const DEFAULT_CAPACITY = 6;
export const MAX_MESSAGE_LENGTH = 300;
export const MAX_NAME_LENGTH = 40;

function tagNumber(tags: string[], prefix: string): number | null {
  for (const tag of tags) {
    const match = tag.trim().toLowerCase().match(new RegExp(`^${prefix}[:\\-_ ]?(\\d+)$`));
    if (match) return Number(match[1]);
  }
  return null;
}

// Box products say how much they hold with a "capacity:5" tag.
export const boxCapacity = (tags: string[]) => tagNumber(tags, "capacity") ?? DEFAULT_CAPACITY;

// Bulky items can claim more than one space with a "slots:2" tag.
export const itemSlots = (tags: string[]) => Math.max(1, tagNumber(tags, "slots") ?? 1);

export const attr = (attributes: CartAttribute[], key: string) => attributes.find((a) => a.key === key)?.value;

export function newBoxId(): string {
  return Math.random().toString(36).slice(2, 6).toUpperCase();
}

type BoxDraft = {
  boxVariantId: string;
  items: { variantId: string; quantity: number }[];
  cardVariantId?: string;
  recipient?: string;
  message?: string;
  sender?: string;
};

export function boxToCartLines(draft: BoxDraft, boxId = newBoxId()): CartLineInput[] {
  const recipient = draft.recipient?.trim();
  const label = recipient ? `Box ${boxId} for ${recipient}` : `Box ${boxId}`;
  const shared = (role: BoxRole): CartAttribute[] => [
    { key: BOX_ATTR.id, value: boxId },
    { key: BOX_ATTR.role, value: role },
    { key: BOX_ATTR.label, value: label },
  ];

  const note: CartAttribute[] = [];
  if (recipient) note.push({ key: BOX_ATTR.recipient, value: recipient });
  if (draft.message?.trim()) note.push({ key: BOX_ATTR.message, value: draft.message.trim() });
  if (draft.sender?.trim()) note.push({ key: BOX_ATTR.sender, value: draft.sender.trim() });

  const lines: CartLineInput[] = [{ merchandiseId: draft.boxVariantId, quantity: 1, attributes: [...shared("box"), ...note] }];
  for (const item of draft.items) {
    lines.push({ merchandiseId: item.variantId, quantity: item.quantity, attributes: shared("item") });
  }
  if (draft.cardVariantId) {
    lines.push({ merchandiseId: draft.cardVariantId, quantity: 1, attributes: shared("card") });
  }
  return lines;
}

// One of our gift boxes: every product in the collection, the note on the first line.
export function giftBoxToCartLines(box: GiftBox, message?: string, boxId = newBoxId()): CartLineInput[] {
  const shared: CartAttribute[] = [
    { key: BOX_ATTR.id, value: boxId },
    { key: BOX_ATTR.role, value: "item" },
    { key: BOX_ATTR.collection, value: box.handle },
    { key: BOX_ATTR.title, value: box.title },
    { key: BOX_ATTR.label, value: `${box.title}, box ${boxId}` },
  ];
  const note = message?.trim();
  return box.items.flatMap(({ variant }, index) =>
    variant
      ? [
          {
            merchandiseId: variant.id,
            quantity: 1,
            attributes: index === 0 && note ? [...shared, { key: BOX_ATTR.message, value: note }] : shared,
          },
        ]
      : [],
  );
}

export type CartBox = {
  id: string;
  // Set for our gift boxes, unset for boxes from the builder.
  collection?: { handle: string; title: string };
  recipient?: string;
  message?: string;
  sender?: string;
  box?: CartLine;
  card?: CartLine;
  items: CartLine[];
  lineIds: string[];
  total: Money;
};

// Splits a cart into the boxes it contains and everything else.
export function groupCart(lines: CartLine[]): { boxes: CartBox[]; singles: CartLine[] } {
  const boxes = new Map<string, CartBox>();
  const singles: CartLine[] = [];

  for (const line of lines) {
    const id = attr(line.attributes, BOX_ATTR.id);
    if (!id) {
      singles.push(line);
      continue;
    }
    let box = boxes.get(id);
    if (!box) {
      box = { id, items: [], lineIds: [], total: { amount: "0", currencyCode: line.cost.totalAmount.currencyCode } };
      boxes.set(id, box);
    }
    box.lineIds.push(line.id);
    const handle = attr(line.attributes, BOX_ATTR.collection);
    if (handle) box.collection ??= { handle, title: attr(line.attributes, BOX_ATTR.title) ?? "Gift box" };
    box.recipient ??= attr(line.attributes, BOX_ATTR.recipient);
    box.message ??= attr(line.attributes, BOX_ATTR.message);
    box.sender ??= attr(line.attributes, BOX_ATTR.sender);
    const role = attr(line.attributes, BOX_ATTR.role);
    if (role === "box") {
      box.box = line;
    } else if (role === "card") {
      box.card = line;
    } else {
      box.items.push(line);
    }
  }

  for (const box of boxes.values()) {
    const all = [box.box, ...box.items, box.card].filter((l): l is CartLine => Boolean(l));
    box.total = sumMoney(all.map((l) => l.cost.totalAmount), box.total.currencyCode);
  }
  return { boxes: [...boxes.values()], singles };
}
